import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Spin, Alert, Empty, Button, Select, Input, Typography,
  Space, Avatar, Badge, Tooltip, Collapse, List, Divider, Tag, Radio
} from 'antd';
import {
  VideoCameraOutlined, UserOutlined, SearchOutlined, FilterOutlined,
  ReloadOutlined, BarChartOutlined, ClockCircleOutlined, BulbOutlined,
  PlayCircleOutlined, DownOutlined, CaretRightOutlined, DownloadOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import { Link } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const GeneratedVideosPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [groupedVideos, setGroupedVideos] = useState([]);

  // Metrics
  const [metrics, setMetrics] = useState({
    totalVideos: 0,
    totalUsers: 0,
    avgVideosPerUser: 0,
    mostCommonPrompt: ''
  });

  // Chart states
  const [videosByDay, setVideosByDay] = useState({
    categories: [],
    series: [{
      name: 'Videos Generated',
      data: []
    }]
  });

  const [promptWordCloud, setPromptWordCloud] = useState({
    labels: [],
    series: []
  });

  // Add time scale state
  const [timeScale, setTimeScale] = useState('daily'); // 'daily', 'weekly', 'monthly'

  useEffect(() => {
    fetchVideoData();
  }, []);

  useEffect(() => {
    if (videoData.length > 0) {
      calculateMetrics();
      prepareChartData();
      groupVideosByUser();
    }
  }, [videoData, searchText, timeScale]);

  const fetchVideoData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllGeneratedVideo', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data) {
        // Process video data
        const processedVideoData = processVideoData(data);
        setVideoData(processedVideoData);
      } else {
        throw new Error('Failed to fetch video data');
      }
    } catch (err) {
      console.error('Error fetching video data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processVideoData = (usersData) => {
    let allVideos = [];
    
    usersData.forEach(userData => {
      const { user, videos } = userData;
      
      if (videos && videos.length > 0) {
        videos.forEach(video => {
          allVideos.push({
            ...video,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${video.video_id}`
          });
        });
      }
    });
    
    return allVideos;
  };

  const calculateMetrics = () => {
    if (videoData.length === 0) return;

    const totalVideos = videoData.length;
    
    // Count unique users
    const uniqueUsers = new Set(videoData.map(vid => vid.user_uid));
    const totalUsers = uniqueUsers.size;
    
    const avgVideosPerUser = totalUsers > 0 ? (totalVideos / totalUsers).toFixed(1) : 0;
    
    // Find most common prompt
    const promptCounts = {};
    videoData.forEach(vid => {
      const prompt = vid.prompt_text || 'Unknown';
      promptCounts[prompt] = (promptCounts[prompt] || 0) + 1;
    });
    
    let mostCommonPrompt = '';
    let maxCount = 0;
    Object.entries(promptCounts).forEach(([prompt, count]) => {
      if (count > maxCount) {
        mostCommonPrompt = prompt;
        maxCount = count;
      }
    });

    setMetrics({
      totalVideos,
      totalUsers,
      avgVideosPerUser,
      mostCommonPrompt
    });
  };

  const prepareChartData = () => {
    if (videoData.length === 0) return;

    // Time scale calculations
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(timeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Initialize time periods
    const timePeriods = {};
    
    if (timeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        timePeriods[dateStr] = 0;
      }
    } else if (timeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        timePeriods[dateStr] = 0;
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        timePeriods[monthYear] = 0;
      }
    }

    // Count videos by time period
    videoData.forEach(vid => {
      try {
        const vidDate = new Date(vid.created_at);
        if (vidDate >= startDate) {
          let dateStr;
          
          if (timeScale === 'daily') {
            dateStr = vidDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (timeScale === 'weekly') {
            const weekNumber = Math.ceil((vidDate.getDate() + vidDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${vidDate.toLocaleString('default', { month: 'short' })} ${vidDate.getFullYear()}`;
          }
          
          if (timePeriods[dateStr] !== undefined) {
            timePeriods[dateStr]++;
          }
        }
      } catch (err) {
        console.error('Error processing video date:', err);
      }
    });

    setVideosByDay({
      categories: Object.keys(timePeriods),
      series: [{
        name: 'Videos Generated',
        data: Object.values(timePeriods)
      }]
    });

    // Word cloud data from prompts
    const wordCounts = {};
    videoData.forEach(vid => {
      if (!vid.prompt_text) return;
      
      const words = vid.prompt_text.split(/\s+/);
      words.forEach(word => {
        if (word.length > 3) {
          const cleanWord = word.toLowerCase().replace(/[^\w\s]/gi, '');
          if (cleanWord) {
            wordCounts[cleanWord] = (wordCounts[cleanWord] || 0) + 1;
          }
        }
      });
    });
    
    // Get top 10 words
    const sortedWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    
    setPromptWordCloud({
      labels: sortedWords.map(([word]) => word),
      series: sortedWords.map(([_, count]) => count)
    });
  };

  const groupVideosByUser = () => {
    let result = [...videoData];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(vid => 
        (vid.user_name && vid.user_name.toLowerCase().includes(lowerSearchText)) ||
        (vid.user_email && vid.user_email.toLowerCase().includes(lowerSearchText)) ||
        (vid.prompt_text && vid.prompt_text.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Group by user
    const grouped = result.reduce((acc, video) => {
      const userId = video.user_uid;
      if (!acc[userId]) {
        acc[userId] = {
          user: {
            uid: userId,
            name: video.user_name,
            email: video.user_email,
            avatar: video.user_avatar
          },
          videos: []
        };
      }
      acc[userId].videos.push(video);
      return acc;
    }, {});
    
    // Sort videos by date (newest first) within each user
    Object.values(grouped).forEach(group => {
      group.videos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    
    // Convert to array and sort by most recent video
    const groupedArray = Object.values(grouped).sort((a, b) => {
      const latestA = a.videos[0]?.created_at || 0;
      const latestB = b.videos[0]?.created_at || 0;
      return new Date(latestB) - new Date(latestA);
    });
    
    setGroupedVideos(groupedArray);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const resetFilters = () => {
    setSearchText('');
  };

  // Chart options
  const lineOptions = {
    chart: {
      type: 'line',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: ['var(--primary-color)'],
    xaxis: {
      categories: videosByDay.categories
    },
    yaxis: {
      title: {
        text: 'Videos'
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.7,
        stops: [0, 100]
      }
    }
  };

  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)', 'var(--warning-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const renderVideoItem = (video) => {
    const formattedDate = new Date(video.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const formattedTime = new Date(video.created_at).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return (
      <List.Item 
        key={video.video_id}
        className="video-item"
        style={{
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: 'var(--background-secondary)',
          marginBottom: '16px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid var(--border-color)'
        }}
      >
        <div className="video-content" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div className="video-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div className="video-details" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="video-title" style={{ fontWeight: '600', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {video.prompt_text || 'No prompt provided'}
              </div>
              <div className="video-subtitle" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>
                Generated on {formattedDate} at {formattedTime}
              </div>
            </div>
            <div className="video-actions">
              <a href={video.video_url} download target="_blank" rel="noopener noreferrer">
                <Button 
                  icon={<DownloadOutlined />} 
                  type="default" 
                  size="small"
                  title="Download video"
                >
                  Download
                </Button>
              </a>
            </div>
          </div>
          
          <div className="video-preview" style={{ width: '100%', textAlign: 'center', marginBottom: '12px' }}>
            <video
              controls
              preload="metadata"
              style={{ 
                maxHeight: '300px', 
                maxWidth: '100%',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              poster={video.image_url}
            >
              <source src={video.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="video-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Tag color="blue" icon={<VideoCameraOutlined />} style={{ borderRadius: '12px' }}>
                {video.video_id.substring(0, 8)}...
              </Tag>
              <Tag color={video.status === 'pending' ? 'orange' : 'green'} style={{ borderRadius: '12px' }}>
                {video.status}
              </Tag>
            </div>
            {video.task_id && (
              <Tag color="purple" style={{ borderRadius: '12px' }}>
                Task: {video.task_id.substring(0, 8)}...
              </Tag>
            )}
          </div>
        </div>
      </List.Item>
    );
  };

  const renderUserPanel = (userGroup, index) => {
    const { user, videos } = userGroup;
    const totalVideos = videos.length;
    
    return (
      <Panel 
        key={user.uid || index}
        header={
          <div className="user-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar 
                src={user.avatar} 
                icon={!user.avatar && <UserOutlined />} 
                size="large"
                style={{ backgroundColor: 'var(--primary-color)' }}
              />
              <div className="user-details">
                <div className="user-name" style={{ fontWeight: '600', fontSize: '16px' }}>{user.name || 'Unknown User'}</div>
                <div className="user-email" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>{user.email || 'No email'}</div>
              </div>
            </div>
            <div className="user-stats" style={{ display: 'flex', gap: '24px' }}>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Videos:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>{totalVideos}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Latest:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>
                  {new Date(videos[0]?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        }
      >
        <List
          className="videos-list"
          dataSource={videos}
          renderItem={renderVideoItem}
          grid={{ 
            gutter: 16, 
            xs: 1, 
            sm: 1, 
            md: 2, 
            lg: 2, 
            xl: 3,
            xxl: 3
          }}
          pagination={{
            pageSize: 6,
            size: 'small',
            showSizeChanger: false
          }}
        />
        <div className="user-actions" style={{ marginTop: '16px', textAlign: 'right' }}>
          <Link to={`/users?uid=${user.uid}`}>
            <Button type="primary" ghost>
              View User Profile
            </Button>
          </Link>
        </div>
      </Panel>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading video data..." spinning={true} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error}
        type="error"
        showIcon
        style={{ margin: '2rem' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <VideoCameraOutlined /> Generated Videos
        </Title>
        <Text type="secondary">
          View and manage AI-generated videos created by users
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalVideos}</div>
              <div className="stat-label">Total Videos</div>
            </div>
            <VideoCameraOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.totalUsers}</div>
              <div className="stat-label">Unique Users</div>
            </div>
            <UserOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{metrics.avgVideosPerUser}</div>
              <div className="stat-label">Avg. Videos Per User</div>
            </div>
            <BarChartOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.mostCommonPrompt ? metrics.mostCommonPrompt.substring(0, 15) + (metrics.mostCommonPrompt.length > 15 ? '...' : '') : 'None'}</div>
              <div className="stat-label">Most Common Prompt</div>
            </div>
            <BulbOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Video Generation Trend" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
            extra={
              <Radio.Group 
                value={timeScale}
                onChange={e => setTimeScale(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            }
          >
            <div style={{ height: '280px', width: '100%' }}>
              {videosByDay.series[0].data.length > 0 ? (
                <Chart
                  options={{
                    ...lineOptions,
                    xaxis: {
                      ...lineOptions.xaxis,
                      categories: videosByDay.categories,
                      labels: {
                        rotate: -45,
                        style: { fontSize: '12px' }
                      }
                    },
                    grid: {
                      padding: {
                        left: 10,
                        right: 10
                      }
                    }
                  }}
                  series={videosByDay.series}
                  type="line"
                  height={300}
                  width="100%"
                />
              ) : (
                <Empty description="No video generation data available" />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Popular Prompt Words" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            <div style={{ height: '280px', width: '100%' }}>
              {promptWordCloud.series.length > 0 ? (
                <Chart
                  options={{
                    ...donutOptions,
                    labels: promptWordCloud.labels
                  }}
                  series={promptWordCloud.series}
                  type="donut"
                  height={300}
                  width="100%"
                />
              ) : (
                <Empty description="No prompt data available" />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter Controls */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search videos, users, or prompts..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchVideoData}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button 
              icon={<FilterOutlined />} 
              onClick={resetFilters}
            >
              Reset Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Videos List */}
      <Card title={`Generated Videos (${groupedVideos.length} users)`}>
        {groupedVideos.length > 0 ? (
          <Collapse 
            defaultActiveKey={[]} 
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            className="user-videos-collapse"
          >
            {groupedVideos.map((userGroup, index) => renderUserPanel(userGroup, index))}
          </Collapse>
        ) : (
          <Empty 
            description="No videos found" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </div>
  );
};

export default GeneratedVideosPage;